/**
 * Blank viewBox margin above a map's topmost territory and below its lowest
 * one, in viewBox units.
 *
 * Maps are authored with a small symmetric margin (~2%) around their shapes.
 * The portrait game map trims both so the board runs flush from the top bar
 * to the controls bar. It keeps half the region border width so the outer
 * stroke isn't clipped. Backdrop-art maps keep their full box: the art fills it.
 *
 * A map's authored `viewBoxPadding` is deliberate breathing room, not blank
 * margin, so that much space is kept on the padded side.
 */
import type { MapDef } from '@warcarp/api-types'

// Keyed by geometry identity: the hydrated map object is rebuilt with every
// state update, and re-parsing every territory path per render is not free.
export interface VerticalTrim { top: number; bottom: number }
const NO_TRIM: VerticalTrim = { top: 0, bottom: 0 }
const cache = new Map<string, VerticalTrim>()
const NUMBER = /-?\d*\.?\d+(?:e-?\d+)?/gi

export function mapVerticalTrim(
  map: Pick<MapDef, 'id' | 'geometryVersion' | 'viewBox' | 'viewBoxPadding' | 'territories' | 'edgeConfig' | 'backgroundImage'>,
): VerticalTrim {
  const pad = map.viewBoxPadding
  const key = `${map.id}:${map.geometryVersion ?? ''}:${map.viewBox.y ?? 0}:${map.viewBox.height}:${pad?.top ?? 0}:${pad?.bottom ?? 0}`
  const cached = cache.get(key)
  if (cached !== undefined) return cached
  let trim = NO_TRIM
  if (!map.backgroundImage) {
    let minY = Infinity
    let maxY = -Infinity
    for (const territory of map.territories) {
      const nums = territory.shape?.match(NUMBER)
      if (!nums) continue
      // Absolute M/L path data: every odd number is a y coordinate.
      for (let i = 1; i < nums.length; i += 2) {
        const yValue = Number(nums[i])
        if (yValue < minY) minY = yValue
        if (yValue > maxY) maxY = yValue
      }
    }
    const strokeHalf = (map.edgeConfig?.regionBorderWidth ?? 4) / 2
    if (Number.isFinite(minY)) {
      const boxTop = map.viewBox.y ?? 0
      const extent = maxY - minY
      trim = {
        top: Math.max(0, minY - boxTop - strokeHalf - extent * (pad?.top ?? 0)),
        bottom: Math.max(0, boxTop + map.viewBox.height - maxY - strokeHalf - extent * (pad?.bottom ?? 0)),
      }
    }
  }
  cache.set(key, trim)
  return trim
}
